import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
    listStoredDocs,
    getStoredDoc,
    deleteStoredDoc,
    type StoredDocument,
} from '~/lib/api'

const MCP_ENDPOINT = 'https://legali-docs.pages.dev/api/mcp'

function generateMcpPrompt(path: string, title: string): string {
    return `Read the stored document "${title}" from the MCP docs store via the docs MCP bridge.

Endpoint: ${MCP_ENDPOINT}
Resource URI: store://${path}

JSON-RPC Request:

\`\`\`json
{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "resources/read",
    "params": {
        "uri": "store://${path}"
    }
}
\`\`\``
}

function CopyMcpPromptButton({ path, title, compact = false }: { path: string; title: string; compact?: boolean }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation()
        const prompt = generateMcpPrompt(path, title)
        await navigator.clipboard.writeText(prompt)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [path, title])

    if (compact) {
        return (
            <button
                onClick={handleCopy}
                className="p-1 text-slate-500 hover:text-blue-400 transition-colors"
                title="Copy MCP prompt to read this document"
            >
                {copied ? (
                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                )}
            </button>
        )
    }

    return (
        <button
            onClick={handleCopy}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
                copied
                    ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                    : 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
            }`}
            title="Copy MCP prompt to read this document"
        >
            {copied ? (
                <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                </>
            ) : (
                <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    Copy MCP Prompt
                </>
            )}
        </button>
    )
}

export const Route = createFileRoute('/store')({
    component: StorePage,
    validateSearch: (search: Record<string, unknown>) => ({
        path: (search.path as string) || undefined,
        source: (search.source as string) || undefined,
        prefix: (search.prefix as string) || undefined,
    }),
})

function StorePage() {
    const navigate = useNavigate()
    const search = useSearch({ from: '/store' })
    const [documents, setDocuments] = useState<StoredDocument[]>([])
    const [selectedDoc, setSelectedDoc] = useState<StoredDocument | null>(null)
    const [loading, setLoading] = useState(true)
    const [docLoading, setDocLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sources, setSources] = useState<string[]>([])
    const [filterSource, setFilterSource] = useState(search.source || '')
    const [filterPrefix, setFilterPrefix] = useState(search.prefix || '')
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

    // Load documents list
    useEffect(() => {
        loadDocuments()
    }, [filterSource, filterPrefix])

    // Load selected document
    useEffect(() => {
        if (search.path) {
            loadDocument(search.path)
        } else {
            setSelectedDoc(null)
        }
    }, [search.path])

    async function loadDocuments() {
        setLoading(true)
        setError(null)
        try {
            const result = await listStoredDocs({
                source: filterSource || undefined,
                prefix: filterPrefix || undefined,
                limit: 500,
            })
            setDocuments(result.documents || [])

            // Extract unique sources
            const uniqueSources = [...new Set(result.documents.map((d) => d.source))]
            setSources(uniqueSources)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load documents')
        } finally {
            setLoading(false)
        }
    }

    async function loadDocument(path: string) {
        setDocLoading(true)
        try {
            const doc = await getStoredDoc(path)
            setSelectedDoc(doc)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load document')
        } finally {
            setDocLoading(false)
        }
    }

    async function handleDelete(path: string) {
        try {
            await deleteStoredDoc(path)
            setDeleteConfirm(null)
            // Reload list and clear selection if deleted doc was selected
            loadDocuments()
            if (search.path === path) {
                navigate({ to: '/store', search: { path: undefined, source: filterSource || undefined, prefix: filterPrefix || undefined } })
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete document')
        }
    }

    function selectDocument(path: string) {
        navigate({
            to: '/store',
            search: { path, source: filterSource || undefined, prefix: filterPrefix || undefined },
        })
    }

    function clearSelection() {
        navigate({
            to: '/store',
            search: { path: undefined, source: filterSource || undefined, prefix: filterPrefix || undefined },
        })
    }

    function formatDate(dateStr: string) {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    function formatSize(bytes: number) {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    return (
        <div className="h-full flex flex-col overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-white">Stored Documents</h1>
                        <p className="text-sm text-slate-400 mt-1">
                            Documents stored via the MCP Store API
                        </p>
                    </div>
                    <a
                        href="/docs/mcp/docs-store-api"
                        className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                        API Docs
                        <span>-&gt;</span>
                    </a>
                </div>

                {/* Filters */}
                <div className="mt-4 flex gap-3">
                    <div className="flex-1 max-w-xs">
                        <label className="text-xs text-slate-400 block mb-1">Filter by source</label>
                        <select
                            value={filterSource}
                            onChange={(e) => setFilterSource(e.target.value)}
                            className="w-full px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                        >
                            <option value="">All sources</option>
                            {sources.map((src) => (
                                <option key={src} value={src}>
                                    {src}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1 max-w-xs">
                        <label className="text-xs text-slate-400 block mb-1">Filter by prefix</label>
                        <input
                            type="text"
                            value={filterPrefix}
                            onChange={(e) => setFilterPrefix(e.target.value)}
                            placeholder="e.g., my-project/"
                            className="w-full px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={loadDocuments}
                            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-sm text-white transition-colors"
                        >
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Error message */}
            {error && (
                <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">
                    {error}
                    <button
                        onClick={() => setError(null)}
                        className="ml-2 text-red-300 hover:text-white"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Document list */}
                <div className="w-96 flex-shrink-0 border-r border-slate-700 overflow-y-auto">
                    {loading ? (
                        <div className="p-4 text-slate-400 text-sm">Loading...</div>
                    ) : documents.length === 0 ? (
                        <div className="p-4 text-slate-400 text-sm">
                            No documents found
                            {filterSource || filterPrefix ? (
                                <button
                                    onClick={() => {
                                        setFilterSource('')
                                        setFilterPrefix('')
                                    }}
                                    className="ml-2 text-blue-400 hover:underline"
                                >
                                    Clear filters
                                </button>
                            ) : null}
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-700/50">
                            {documents.map((doc) => (
                                <div
                                    key={doc.path}
                                    className={`p-3 cursor-pointer hover:bg-slate-800/50 transition-colors ${
                                        search.path === doc.path ? 'bg-blue-600/10 border-l-2 border-blue-500' : ''
                                    }`}
                                    onClick={() => selectDocument(doc.path)}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-sm font-medium text-white truncate">
                                                {doc.title}
                                            </h3>
                                            <p className="text-xs text-slate-400 truncate mt-0.5">
                                                {doc.path}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <CopyMcpPromptButton path={doc.path} title={doc.title} compact />
                                            {deleteConfirm === doc.path ? (
                                                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handleDelete(doc.path)}
                                                        className="px-2 py-0.5 bg-red-600 text-white text-xs rounded hover:bg-red-500"
                                                    >
                                                        Yes
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(null)}
                                                        className="px-2 py-0.5 bg-slate-600 text-white text-xs rounded hover:bg-slate-500"
                                                    >
                                                        No
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setDeleteConfirm(doc.path)
                                                    }}
                                                    className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                                                    title="Delete document"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                        <span className="px-1.5 py-0.5 bg-slate-700 rounded">
                                            {doc.source}
                                        </span>
                                        <span>{formatSize(doc.size)}</span>
                                        <span>{formatDate(doc.updatedAt)}</span>
                                    </div>
                                    {doc.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {doc.tags.slice(0, 3).map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                            {doc.tags.length > 3 && (
                                                <span className="text-xs text-slate-500">
                                                    +{doc.tags.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Document viewer */}
                <div className="flex-1 overflow-y-auto">
                    {docLoading ? (
                        <div className="p-6 text-slate-400">Loading document...</div>
                    ) : selectedDoc ? (
                        <div className="p-6">
                            {/* Document header */}
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h2 className="text-lg font-semibold text-white">
                                        {selectedDoc.title}
                                    </h2>
                                    <p className="text-sm text-slate-400 mt-1 font-mono">
                                        {selectedDoc.path}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CopyMcpPromptButton path={selectedDoc.path} title={selectedDoc.title} />
                                    <button
                                        onClick={clearSelection}
                                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Metadata */}
                            <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-6 pb-4 border-b border-slate-700">
                                <div>
                                    <span className="text-slate-500">Source:</span>{' '}
                                    <span className="text-white">{selectedDoc.source}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Size:</span>{' '}
                                    <span className="text-white">{formatSize(selectedDoc.size)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Created:</span>{' '}
                                    <span className="text-white">{formatDate(selectedDoc.createdAt)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Updated:</span>{' '}
                                    <span className="text-white">{formatDate(selectedDoc.updatedAt)}</span>
                                </div>
                            </div>

                            {/* Tags */}
                            {selectedDoc.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {selectedDoc.tags.map((tag) => (
                                        <span
                                            key={tag}
                                            className="px-2 py-1 bg-blue-500/20 text-blue-400 text-sm rounded"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Content */}
                            <div className="markdown-body prose prose-invert max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {selectedDoc.content || ''}
                                </ReactMarkdown>
                            </div>
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-slate-400">
                            <div className="text-center">
                                <p>Select a document to view its contents</p>
                                <p className="text-sm mt-2">
                                    {documents.length} document{documents.length !== 1 ? 's' : ''} available
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
